import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Plus, X, Edit2, Trash2, Folder, ChevronRight, ChevronDown, GripVertical, FolderPlus, ExternalLink } from 'lucide-react'
import { bookmarkAPI, widgetAPI } from '../../services/api'
import { useModalClose } from '../../hooks/useModalClose'
import toast from 'react-hot-toast'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface BookmarksWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Bookmark {
  id: number
  title: string
  url: string
  folder: string
  position: number
  icon_url?: string
}

export default function BookmarksWidget({ widget, isExpanded }: BookmarksWidgetProps) {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAddFolderModal, setShowAddFolderModal] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
    // Load saved expanded folders state, or default to 'General' open
    if (widget.config?.expandedFolders && Array.isArray(widget.config.expandedFolders)) {
      return new Set(widget.config.expandedFolders)
    }
    return new Set(['General'])
  })
  const [activeId, setActiveId] = useState<number | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [newBookmark, setNewBookmark] = useState({
    title: '',
    url: '',
    folder: 'General',
  })
  
  // Modal close handlers
  const { handleBackdropClick: handleAddModalClose } = useModalClose(() => setShowAddModal(false))
  const { handleBackdropClick: handleFolderModalClose } = useModalClose(() => setShowAddFolderModal(false))

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const { data: bookmarks = [] } = useQuery({
    queryKey: ['bookmarks'],
    queryFn: async () => {
      const { data } = await bookmarkAPI.getAll()
      return data
    },
  })

  const { data: folders = [] } = useQuery({
    queryKey: ['bookmark-folders'],
    queryFn: async () => {
      const { data } = await bookmarkAPI.getFolders()
      return data.length > 0 ? data : ['General']
    },
  })

  const handleAddBookmark = async () => {
    if (!newBookmark.title || !newBookmark.url) {
      toast.error('Title and URL are required')
      return
    }

    try {
      await bookmarkAPI.create(newBookmark)
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
      queryClient.invalidateQueries({ queryKey: ['bookmark-folders'] })
      toast.success('Bookmark added!')
      setShowAddModal(false)
      setNewBookmark({ title: '', url: '', folder: 'General' })
    } catch (error) {
      toast.error('Failed to add bookmark')
    }
  }

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required')
      return
    }

    try {
      await bookmarkAPI.createFolder(newFolderName)
      queryClient.invalidateQueries({ queryKey: ['bookmark-folders'] })
      toast.success('Folder created!')
      setShowAddFolderModal(false)
      setNewFolderName('')
      // Expand the new folder
      setExpandedFolders(prev => new Set([...prev, newFolderName]))
    } catch (error) {
      toast.error('Failed to create folder')
    }
  }

  const handleDeleteBookmark = async (id: number) => {
    if (!confirm('Are you sure you want to delete this bookmark?')) return

    try {
      await bookmarkAPI.delete(id)
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
      toast.success('Bookmark deleted!')
    } catch (error) {
      toast.error('Failed to delete bookmark')
    }
  }

  const handleDeleteFolder = async (folderName: string) => {
    const bookmarksInFolder = bookmarks.filter((b: Bookmark) => b.folder === folderName)
    
    if (bookmarksInFolder.length > 0) {
      if (!confirm(`This folder contains ${bookmarksInFolder.length} bookmark(s). Move them to "General" folder and delete this folder?`)) {
        return
      }
    } else if (!confirm(`Are you sure you want to delete the "${folderName}" folder?`)) {
      return
    }

    try {
      await bookmarkAPI.deleteFolder(folderName)
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
      queryClient.invalidateQueries({ queryKey: ['bookmark-folders'] })
      toast.success('Folder deleted!')
    } catch (error) {
      toast.error('Failed to delete folder')
    }
  }

  const handleOpenBookmark = (url: string) => {
    window.open(url.startsWith('http') ? url : `https://${url}`, '_blank')
  }

  const toggleFolder = async (folder: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(folder)) {
        newSet.delete(folder)
      } else {
        newSet.add(folder)
      }
      
      // Save the expanded folders state to widget config
      const expandedArray = Array.from(newSet)
      const newConfig = {
        ...widget.config,
        expandedFolders: expandedArray
      }
      
      // Save to backend asynchronously (don't await to keep UI responsive)
      widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      }).then(() => {
        widget.config = newConfig
      }).catch(error => {
        console.error('Failed to save folder state:', error)
      })
      
      return newSet
    })
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    const activeBookmark = bookmarks.find((b: Bookmark) => b.id === active.id)
    if (!activeBookmark) return

    // Check if dropped on a folder
    const overFolder = folders.find((f: string) => `folder-${f}` === over.id)
    
    if (overFolder) {
      // Move bookmark to folder
      if (activeBookmark.folder !== overFolder) {
        try {
          await bookmarkAPI.update(activeBookmark.id, { folder: overFolder })
          queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
          toast.success(`Moved to ${overFolder}`)
          // Expand the target folder
          setExpandedFolders(prev => new Set([...prev, overFolder]))
        } catch (error) {
          toast.error('Failed to move bookmark')
        }
      }
      return
    }

    // Reorder within the same folder
    if (active.id !== over.id) {
      const oldIndex = bookmarks.findIndex((b: Bookmark) => b.id === active.id)
      const newIndex = bookmarks.findIndex((b: Bookmark) => b.id === over.id)

      const activeItem = bookmarks[oldIndex]
      const overItem = bookmarks[newIndex]

      // Only reorder if in the same folder
      if (activeItem.folder === overItem.folder) {
        const reordered = arrayMove(bookmarks, oldIndex, newIndex)
        
        // Update positions
        try {
          await Promise.all(
            reordered.map((bookmark: Bookmark, index: number) =>
              bookmarkAPI.update(bookmark.id, { position: index })
            )
          )
          queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
        } catch (error) {
          toast.error('Failed to reorder bookmarks')
        }
      }
    }
  }

  // Group bookmarks by folder
  const bookmarksByFolder = bookmarks.reduce((acc: any, bookmark: Bookmark) => {
    if (!acc[bookmark.folder]) {
      acc[bookmark.folder] = []
    }
    acc[bookmark.folder].push(bookmark)
    return acc
  }, {})

  // Sort bookmarks within each folder by position
  Object.keys(bookmarksByFolder).forEach(folder => {
    bookmarksByFolder[folder].sort((a: Bookmark, b: Bookmark) => a.position - b.position)
  })

  const activeBookmark = bookmarks.find((b: Bookmark) => b.id === activeId)

  return (
    <div className="h-full flex flex-col p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Bookmarks</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddFolderModal(true)}
            className="p-1.5 hover:bg-gray-100 rounded transition"
            title="Add folder"
          >
            <FolderPlus size={18} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="p-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded transition"
            title="Add bookmark"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {/* Bookmarks List with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-y-auto space-y-1">
          {folders.map((folder: string) => (
            <div key={folder}>
              {/* Folder Header */}
              <SortableContext
                items={[`folder-${folder}`]}
                strategy={verticalListSortingStrategy}
              >
                <div
                  id={`folder-${folder}`}
                  className="flex items-center justify-between p-2 rounded hover:bg-gray-50 transition group"
                  style={{ backgroundColor: expandedFolders.has(folder) ? 'var(--color-surface)' : 'transparent' }}
                >
                  <button
                    onClick={() => toggleFolder(folder)}
                    className="flex items-center gap-2 flex-1"
                  >
                    {expandedFolders.has(folder) ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                    <Folder size={16} />
                    <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {folder}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      ({bookmarksByFolder[folder]?.length || 0})
                    </span>
                  </button>
                  {folder !== 'General' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteFolder(folder)
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </SortableContext>

              {/* Folder Contents */}
              {expandedFolders.has(folder) && bookmarksByFolder[folder] && (
                <SortableContext
                  items={bookmarksByFolder[folder].map((b: Bookmark) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="ml-6 mt-1 space-y-1">
                    {bookmarksByFolder[folder].map((bookmark: Bookmark) => (
                      <SortableBookmarkItem
                        key={bookmark.id}
                        bookmark={bookmark}
                        onDelete={handleDeleteBookmark}
                        onOpen={handleOpenBookmark}
                      />
                    ))}
                  </div>
                </SortableContext>
              )}
            </div>
          ))}

          {folders.length === 0 && (
            <div className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
              <p className="text-sm">No folders yet. Create one to get started!</p>
            </div>
          )}
        </div>
      </DndContext>

      {/* Add Bookmark Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center" onClick={handleAddModalClose}>
          <div className="rounded-lg p-6 w-full max-w-md" style={{ backgroundColor: 'var(--color-widget-bg)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Add Bookmark</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Title *</label>
                <input
                  type="text"
                  value={newBookmark.title}
                  onChange={(e) => setNewBookmark({ ...newBookmark, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ 
                    backgroundColor: 'var(--color-surface)', 
                    borderColor: 'var(--color-border)', 
                    color: 'var(--color-text-primary)' 
                  }}
                  placeholder="My Bookmark"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>URL *</label>
                <input
                  type="url"
                  value={newBookmark.url}
                  onChange={(e) => setNewBookmark({ ...newBookmark, url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ 
                    backgroundColor: 'var(--color-surface)', 
                    borderColor: 'var(--color-border)', 
                    color: 'var(--color-text-primary)' 
                  }}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Folder</label>
                <select
                  value={newBookmark.folder}
                  onChange={(e) => setNewBookmark({ ...newBookmark, folder: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ 
                    backgroundColor: 'var(--color-surface)', 
                    borderColor: 'var(--color-border)', 
                    color: 'var(--color-text-primary)' 
                  }}
                >
                  {folders.map((folder: string) => (
                    <option key={folder} value={folder}>
                      {folder}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddBookmark}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Add Bookmark
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 rounded-lg transition"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Folder Modal */}
      {showAddFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center" onClick={handleFolderModalClose}>
          <div className="rounded-lg p-6 w-full max-w-md" style={{ backgroundColor: 'var(--color-widget-bg)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Create Folder</h3>
              <button onClick={() => setShowAddFolderModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Folder Name *</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ 
                    backgroundColor: 'var(--color-surface)', 
                    borderColor: 'var(--color-border)', 
                    color: 'var(--color-text-primary)' 
                  }}
                  placeholder="Work, Personal, etc."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddFolder}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Create Folder
              </button>
              <button
                onClick={() => setShowAddFolderModal(false)}
                className="px-4 py-2 rounded-lg transition"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Sortable Bookmark Item Component
function SortableBookmarkItem({ bookmark, onDelete, onOpen }: { bookmark: Bookmark; onDelete: (id: number) => void; onOpen: (url: string) => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bookmark.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition group"
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical size={14} style={{ color: 'var(--color-text-secondary)' }} />
      </div>

      {/* Bookmark Content */}
      <button
        onClick={() => onOpen(bookmark.url)}
        className="flex items-center gap-2 flex-1 min-w-0 text-left"
      >
        <div className="w-5 h-5 rounded flex items-center justify-center text-xs font-semibold flex-shrink-0" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}>
          {bookmark.title.charAt(0).toUpperCase()}
        </div>
        <span className="text-sm truncate" style={{ color: 'var(--color-text-primary)' }}>
          {bookmark.title}
        </span>
        <ExternalLink size={12} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition" style={{ color: 'var(--color-text-secondary)' }} />
      </button>

      {/* Delete Button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete(bookmark.id)
        }}
        className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded transition flex-shrink-0"
      >
        <Trash2 size={14} />
      </button>
    </div>
  )
}
